import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
}

export default function ContactPage() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showWeChatQR, setShowWeChatQR] = useState(false);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://documogul.com';
    
    document.title = '联系我们 - 星允信息 | 专业PDF解决方案咨询';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '联系星允信息获取专业的PDF解决方案咨询服务。我们提供7x24小时技术支持，地址：广东省广州市天河区灵山东路5号2层，电话：400-838-9650。');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '联系我们,技术支持,客户服务,咨询服务,广州PDF服务,星允信息联系方式');
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/contact`);

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "联系我们 - 星允信息",
      "description": "联系星允信息获取专业的PDF解决方案咨询服务",
      "url": `${siteUrl}/contact`,
      "mainEntity": {
        "@type": "Organization",
        "name": "星允信息科技有限公司",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "灵山东路5号2层",
          "addressLocality": "广州市",
          "addressRegion": "广东省",
          "addressCountry": "CN"
        },
        "telephone": "400-838-9650",
        "email": "hello@documogul.com"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证必填字段
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    // 验证留言长度
    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // 使用 application/x-www-form-urlencoded 格式提交
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4c28tj62rkouuf3qjk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        // 重置表单
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: '公司地址',
      content: '广东省广州市天河区灵山东路5号2层',
      link: null
    },
    {
      icon: 'ri-phone-line',
      title: '联系电话',
      content: '400-838-9650',
      link: 'tel:400-838-9650'
    },
    {
      icon: 'ri-mail-line',
      title: '电子邮箱',
      content: 'hello@documogul.com',
      link: 'mailto:hello@documogul.com'
    },
    {
      icon: 'ri-time-line',
      title: '工作时间',
      content: '周一至周五 9:00 - 18:00',
      link: null
    }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">联系我们</h1>
            <p className="text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed">
              我们随时准备为您提供帮助。无论您有任何问题或需求，请随时与我们联系
            </p>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactInfo.map((info) => (
                <div key={info.title} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${info.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{info.title}</h3>
                  {info.link ? (
                    <a href={info.link} className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-gray-600">{info.content}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form & Map */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">联络我们</h2>
                <p className="text-gray-600 mb-8">
                  填写下面的表单，我们的团队将在24小时内回复您
                </p>
                
                <form
                  id="contact-form"
                  data-readdy-form
                  className="space-y-6"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      电子邮箱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      联系电话
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="+86 138 0000 0000"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      公司名称
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="请输入公司名称"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      咨询主题
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">请选择咨询主题</option>
                      <option value="产品咨询">产品咨询</option>
                      <option value="技术支持">技术支持</option>
                      <option value="商务合作">商务合作</option>
                      <option value="其他问题">其他问题</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      留言内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="请详细描述您的问题或需求（最多500字）"
                    />
                    <div className="text-right text-sm text-gray-500 mt-1">
                      {formData.message.length}/500
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? '发送中...' : '发送消息'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-check-circle-line text-xl mr-2"></i>
                        <span>消息发送成功！我们会尽快与您联系。</span>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line text-xl mr-2"></i>
                        <span>发送失败，请稍后重试或直接联系我们。</span>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Map & Additional Info */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">我们的位置</h2>
                <p className="text-gray-600 mb-8">
                  欢迎访问我们的办公室，我们期待与您面对面交流
                </p>

                {/* 百度地图 */}
                <div className="rounded-xl overflow-hidden shadow-lg mb-8">
                  <iframe
                    src="https://map.baidu.com/search/%E4%B8%AD%E5%9B%BD%E5%B9%BF%E5%B7%9E%E5%B8%82%E5%A4%A9%E6%B2%B3%E5%8C%BA%E7%81%B5%E5%B1%B1%E4%B8%9C%E8%B7%AF5%E5%8F%B72%E5%B1%82/@13550825.17,2636891.5,19z?querytype=s&da_src=shareurl&wd=%E4%B8%AD%E5%9B%BD%E5%B9%BF%E5%B7%9E%E5%B8%82%E5%A4%A9%E6%B2%B3%E5%8C%BA%E7%81%B5%E5%B1%B1%E4%B8%9C%E8%B7%AF5%E5%8F%B72%E5%B1%82&c=257&src=0&pn=0&sug=0&l=19&b=(13550675,2636741;13550975,2637041)&from=webmap&biz_forward=%7B%22scaler%22:1,%22styles%22:%22pl%22%7D"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    title="公司位置地图"
                  ></iframe>
                </div>

                {/* Social Media */}
                <div className="bg-gray-50 rounded-xl p-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">关注我们</h3>
                  <p className="text-gray-600 mb-6">
                    在社交媒体上关注我们，获取最新产品动态和行业资讯
                  </p>
                  <div className="flex gap-4">
                    <button 
                      onClick={() => setShowWeChatQR(true)}
                      className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-gray-600 hover:text-teal-600 hover:shadow-md transition-all cursor-pointer"
                    >
                      <i className="ri-wechat-line text-2xl"></i>
                    </button>
                    <a href="https://github.com/datalogics" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-gray-600 hover:text-teal-600 hover:shadow-md transition-all cursor-pointer">
                      <i className="ri-github-line text-2xl"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">常见问题</h2>
              <p className="text-gray-600">
                以下是一些客户经常询问的问题，或许能帮助您快速找到答案
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  question: 'Adobe PDF Library SDK支持哪些平台？',
                  answer: 'Adobe PDF Library SDK支持Windows、MacOS、Linux，提供跨平台的一致体验。'
                },
                {
                  question: 'Adobe PDF Library SDK 支持哪些开发语言？',
                  answer: 'Adobe PDF Library SDK支持 C、C++、.NET、.NET Framework 和 Java。'
                },
                {
                  question: '是否提供免费试用？',
                  answer: '是的，我们为所有产品提供30天免费试用期，让您充分体验产品功能后再做决定。'
                },
                {
                  question: '定价方案是怎样的？',
                  answer: '我们提供灵活的定价方案，包括按使用量计费和企业订阅等多种选择。具体价格请联系我们的销售团队。'
                }
              ].map((faq, index) => (
                <details key={index} className="bg-white rounded-lg shadow-sm group">
                  <summary className="px-6 py-4 cursor-pointer list-none flex items-center justify-between font-semibold text-gray-900 hover:text-teal-600 transition-colors">
                    <span>{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-xl group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-6 pb-4 text-gray-600">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />

      {/* WeChat QR Code Modal */}
      {showWeChatQR && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowWeChatQR(false)}
        >
          <div 
            className="bg-white rounded-2xl p-8 max-w-md w-full relative animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowWeChatQR(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-wechat-line text-3xl text-teal-600"></i>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-2">微信公众号</h3>
              <p className="text-gray-600 mb-6">扫描二维码关注我们</p>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <img 
                  src="https://static.readdy.ai/image/8dfcde88d6d872f7d495e2c3222a324c/24a3e17d299d19953469e820a3f2ff08.jpeg"
                  alt="微信公众号二维码"
                  className="w-full max-w-xs mx-auto"
                />
              </div>
              
              <p className="text-sm text-gray-500">
                关注我们获取最新产品动态和行业资讯
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
